import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import { company } from '~/company/Company';

import { useModal } from '../../hooks/useModal';
import type { ModalProps } from './interface';

/** Below the `md` breakpoint (phones) the body fills the whole screen, with rounded top corners. */
const FULLSCREEN_MOBILE_CLASS =
  'max-md:inset-x-0! max-md:top-0! max-md:bottom-0! max-md:w-full! max-md:max-w-none! max-md:transform-none! max-md:rounded-t-[28px]! max-md:rounded-b-none! max-md:p-0!';

export default observer(
  ({
    isShow,
    children,
    isBackdrop = true,
    onBackdropDismiss,
    className,
    containerClassName,
    mode = 'centered',
    isDismissible = true,
    isEscapeLikeBackdrop = false,
  }: ModalProps) => {
    const { closeModal } = useModal();
    const refModal = useRef<HTMLDivElement>(null);

    const handleBackdropDismiss = () => {
      if (!isDismissible) {
        return;
      }
      if (onBackdropDismiss) {
        onBackdropDismiss(closeModal);
      } else {
        closeModal();
      }
    };

    const backdropDismissRef = useRef(handleBackdropDismiss);
    backdropDismissRef.current = handleBackdropDismiss;

    useEffect(() => {
      const closeByEsc = (e: KeyboardEvent) => {
        if (!isDismissible) {
          return;
        }
        if (e.key === 'Escape') {
          if (isEscapeLikeBackdrop) {
            backdropDismissRef.current();
          } else {
            closeModal();
          }
        }
      };

      document.addEventListener('keydown', closeByEsc);
      return () => document.removeEventListener('keydown', closeByEsc);
    }, [isDismissible, isEscapeLikeBackdrop]);

    return (
      <CSSTransition
        in={isShow}
        timeout={{ enter: 350, exit: 250 }}
        mountOnEnter
        unmountOnExit
        classNames="modal"
        nodeRef={refModal}
      >
        <div className={classes(['modal', containerClassName])} ref={refModal}>
          <div
            className={classes(['modal__bg', !isBackdrop && '_transparent'])}
            onClick={handleBackdropDismiss}
          />
          <div
            className={classes([
              'modal__body',
              mode === 'bottom-sheet' && '_bottom-sheet',
              mode === 'fullscreen-mobile' && FULLSCREEN_MOBILE_CLASS,
              className,
            ])}
            data-company={company.name}
          >
            {children}
          </div>
        </div>
      </CSSTransition>
    );
  },
);
